import { HttpCacheManager, withCache } from '@ngneat/cashew';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { environment } from '@environments/environment';

import { API } from '@models/API';

import { platformAPI } from '@services/API/endpoints';

/** Matches the backend's default, so the first page is one round trip. */
export const DEFAULT_PAGE_SIZE = 50;

@Injectable({
  providedIn: 'root',
})
export class OperationsChartsAPI {
  private http = inject(HttpClient);
  public cacheKey = 'operations';

  /**
   * Every cache key this service has handed to cashew.
   *
   * Tracked because the keys are no longer predictable from outside: they carry
   * the page number, so how many exist depends on how large the catalogue was
   * the last time it was walked. `clearCache` is the only correct way to drop
   * them all.
   */
  private readonly cachedKeys = new Set<string>();

  private pageKey(page: number, size: number) {
    return `${this.cacheKey}-${page}-${size}`;
  }

  /**
   * One page of the facilities catalogue.
   *
   * LEN-2049: the endpoint is paginated now. Each page caches under its own
   * key — a single `cacheKey` would make every page overwrite the one before
   * it and defeat the TTL this catalogue relies on.
   */
  public getAll(
    page = 1,
    size = DEFAULT_PAGE_SIZE,
    loaderOptIn: boolean | string = true
  ) {
    const key = this.pageKey(page, size);
    this.cachedKeys.add(key);

    return this.http.get<API.Trusts.Operations.Charts.Response>(
      platformAPI.trusts().operations.allCharts,
      {
        params: { page, size },
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        context: withCache({
          ttl: environment.CACHE_LIFETIME,
          key,
        }),
      }
    );
  }

  /**
   * Drops every page this service has cached.
   *
   * One `delete` per key, deliberately. `HttpCacheManager.delete` takes
   * `string | CacheBucket` and only iterates for a `CacheBucket`; handing it an
   * array falls through to a `Map.delete` keyed by that array, which never
   * matches and reports nothing — it would look applied and change nothing.
   *
   * Deleting `cacheKey` alone has the same problem since LEN-2049: no page is
   * stored under the bare `'operations'` key any more, so a facility edit would
   * keep serving the pre-edit catalogue for the rest of the TTL.
   */
  public clearCache(cacheManager: HttpCacheManager) {
    this.cachedKeys.forEach(key => cacheManager.delete(key));
    this.cachedKeys.clear();
  }
}
